import styled from "@emotion/styled";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import type { TenantListItemDto } from "Infrastructure/Api/Api";
import { ConfirmDialog } from "Shared/Components/ConfirmDialog";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import { useDeleteTenantMutation } from "Tenants/Api/Mutations/useDeleteTenantMutation";
import { useTenantsQuery } from "Tenants/Api/Queries/useTenantsQuery";
import { TenantDetailsDialog } from "Tenants/Components/TenantDetailsDialog";
import { type FC, useState } from "react";

const StyledCell = styled(Box)``;

const StyledTextCell = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StyledActionsCell = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: flex-end;
`;

type Props = {
	onEdit: (tenant: TenantListItemDto) => void;
};

export const TenantsTable: FC<Props> = ({ onEdit }) => {
	const { data, isLoading } = useTenantsQuery();
	const deleteMutation = useDeleteTenantMutation();
	const [tenantToDelete, setTenantToDelete] =
		useState<TenantListItemDto | null>(null);
	const [detailsDialog, setDetailsDialog] = useState<{
		open: boolean;
		tenantId: number;
		tenantCode: string;
		type: "users" | "configurations";
	}>({ open: false, tenantId: 0, tenantCode: "", type: "users" });

	const tenants: TenantListItemDto[] = data?.items ?? [];

	const handleEdit = (tenant: TenantListItemDto) => {
		onEdit(tenant);
	};

	const handleDeleteClick = (tenant: TenantListItemDto) => {
		setTenantToDelete(tenant);
	};

	const handleConfirmDelete = async () => {
		if (tenantToDelete) {
			await deleteMutation.mutateAsync(tenantToDelete.tenantID);
			setTenantToDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setTenantToDelete(null);
	};

	return (
		<>
			<BlDataTable
				rows={tenants.map((x, index) => ({ data: x }))}
				rowEntityID="code"
				isLoading={isLoading}
				getHeaderCell={(column) => <div>{column.label ?? column.id}</div>}
				columns={[
					{ id: "code", label: "Code", fillRow: true },
					{ id: "userCount", label: "Users" },
					{ id: "extractionConfigurationCount", label: "Configurations" },
					{
						id: "action",
						label: "Actions",
						maxWidth: 0,
						justifySelf: "flex-end",
					},
				]}
				rowsPerPage={10}
				getCell={(id, data) => {
					switch (id) {
						case "code":
							return <StyledTextCell>{data.code}</StyledTextCell>;
						case "userCount":
							return (
								<StyledCell>
									<Tooltip title="Click to view users">
										<Chip
											label={data.userCount}
											color="primary"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setDetailsDialog({
													open: true,
													tenantId: data.tenantID,
													tenantCode: data.code,
													type: "users",
												});
											}}
											sx={{ cursor: "pointer" }}
										/>
									</Tooltip>
								</StyledCell>
							);
						case "extractionConfigurationCount":
							return (
								<StyledCell>
									<Tooltip title="Click to view configurations">
										<Chip
											label={data.extractionConfigurationCount}
											color="secondary"
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												setDetailsDialog({
													open: true,
													tenantId: data.tenantID,
													tenantCode: data.code,
													type: "configurations",
												});
											}}
											sx={{ cursor: "pointer" }}
										/>
									</Tooltip>
								</StyledCell>
							);
						case "action":
							return (
								<StyledActionsCell>
									<IconButton
										size="small"
										color="primary"
										onClick={(e) => {
											e.stopPropagation();
											handleEdit(data);
										}}
										title="Edit"
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteClick(data);
										}}
										title="Delete"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</StyledActionsCell>
							);
						default:
							return <StyledCell />;
					}
				}}
			/>
			{tenantToDelete && (
				<ConfirmDialog
					open={!!tenantToDelete}
					title="Delete Tenant"
					message={`Are you sure you want to delete the tenant "${tenantToDelete.code}"? This action cannot be undone.`}
					confirmText="Delete"
					onConfirm={handleConfirmDelete}
					onCancel={handleCancelDelete}
					isLoading={deleteMutation.isPending}
				/>
			)}
			<TenantDetailsDialog
				open={detailsDialog.open}
				onClose={() =>
					setDetailsDialog({
						open: false,
						tenantId: 0,
						tenantCode: "",
						type: "users",
					})
				}
				tenantId={detailsDialog.tenantId}
				tenantCode={detailsDialog.tenantCode}
				type={detailsDialog.type}
			/>
		</>
	);
};
